import { z } from 'zod';
import { TimestampSchema } from './common.js';
/**
 * Встречи из Fathom.
 *
 * Две группы схем. `Fathom*Response` описывают то, что отдаёт `api.fathom.ai/external/v1`,
 * и объявлены как loose: сервис добавляет поля без предупреждения, и незнакомый ключ не должен
 * ронять запрос. `Meeting` — модель Jisy: из ответа берётся только то, что показывает таблица,
 * а поля с содержимым встречи (расшифровка, конспект, задачи) не запрашиваются вовсе — они
 * считаются «тяжёлыми» и режут и без того небольшой лимит обращений.
 */
// ── Ответ Fathom ─────────────────────────────────────────────────────────────
export const FathomPersonSchema = z.looseObject({
    name: z.string().nullish(),
    email: z.string().nullish(),
    email_domain: z.string().nullish(),
    team: z.string().nullish(),
    is_external: z.boolean().nullish(),
});
export const FathomMeetingSchema = z.looseObject({
    /** Идентификатор записи. Приходит числом, внутри Jisy живёт строкой. */
    recording_id: z.union([z.number(), z.string()]),
    /** Название встречи в Fathom и название события в календаре — это разные строки. */
    title: z.string().nullish(),
    meeting_title: z.string().nullish(),
    meeting_type: z.string().nullish(),
    /** Ссылка на запись в Fathom; `share_url` открывается и без входа. */
    url: z.string().nullish(),
    share_url: z.string().nullish(),
    /** Ссылка на саму конференцию: Zoom, Meet, Teams. */
    meeting_url: z.string().nullish(),
    created_at: z.string().nullish(),
    scheduled_start_time: z.string().nullish(),
    scheduled_end_time: z.string().nullish(),
    recording_start_time: z.string().nullish(),
    recording_end_time: z.string().nullish(),
    transcript_language: z.string().nullish(),
    calendar_invitees: z.array(FathomPersonSchema).nullish(),
    recorded_by: FathomPersonSchema.nullish(),
});
export const FathomMeetingsResponseSchema = z.looseObject({
    items: z.array(FathomMeetingSchema),
    /** Следующая страница; `null` означает, что выдача закончилась. */
    next_cursor: z.string().nullish(),
    limit: z.number().int().nullish(),
});
// ── Модель Jisy ──────────────────────────────────────────────────────────────
export const MeetingPersonSchema = z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    /** Участник не из домена организации: в таблице такие считаются отдельно. */
    isExternal: z.boolean(),
});
export const MeetingSchema = z.object({
    id: z.string(),
    /**
     * Название встречи: своё, если его дали в Jisy, иначе из Fathom. Пустое в Fathom
     * заменяется названием события календаря.
     */
    title: z.string(),
    /**
     * Название из Fathom без нашего переопределения. Нужно интерфейсу, чтобы показать,
     * подо что встреча записана на самом деле, и чтобы вернуть исходное имя.
     */
    sourceTitle: z.string(),
    /** Название задано в Jisy: в Fathom оно не уходит — её внешний API только на чтение. */
    renamed: z.boolean(),
    meetingType: z.string().nullable(),
    /**
     * Когда встреча состоялась. Берётся время записи, а не запланированное: перенос на полчаса
     * в календаре не отражается, а запись начинается ровно тогда, когда люди собрались.
     */
    startedAt: TimestampSchema.nullable(),
    endedAt: TimestampSchema.nullable(),
    /** Длительность записи в минутах; `null`, если у записи нет обеих границ. */
    durationMinutes: z.number().int().nonnegative().nullable(),
    recordedBy: MeetingPersonSchema.nullable(),
    invitees: z.array(MeetingPersonSchema),
    externalInvitees: z.number().int().nonnegative(),
    language: z.string().nullable(),
    url: z.string().nullable(),
    shareUrl: z.string().nullable(),
    /**
     * Раздел, своё название и признак архива — единственные поля, которых нет в Fathom:
     * они живут в таблице `meeting_meta` и общие для всего тенанта.
     */
    projectId: z.string().nullable(),
    archived: z.boolean(),
});
/**
 * Страница встреч. Своя, а не общая `pageOf`: Fathom не сообщает, сколько всего записей,
 * и подставлять сюда `total: null` значило бы обещать поле, которого не будет никогда.
 */
export const MeetingPageSchema = z.object({
    items: z.array(MeetingSchema),
    nextCursor: z.string().nullable(),
});
/**
 * Размер страницы задаёт сам Fathom: параметра `limit` у списка встреч нет, есть только курсор.
 * Поэтому прокрутка таблицы листает ровно теми порциями, какими отвечает сервис.
 */
/**
 * Состояние подключения к Fathom. Сам ключ наружу не отдаётся никогда — только признак
 * того, что он есть, и когда его в последний раз принимал Fathom.
 */
export const FathomConnectionSchema = z.object({
    connected: z.boolean(),
    connectedAt: TimestampSchema.nullable(),
    /** Ключ был отозван или перестал действовать: интерфейс просит ввести новый. */
    revoked: z.boolean(),
});
export const FathomKeyUpdateSchema = z.object({
    apiKey: z.string().trim().min(8).max(512),
});
/** Привязка встречи к разделу. `null` снимает связь, а не заводит пустую строку. */
export const MeetingSectionUpdateSchema = z.object({
    projectId: z.string().trim().min(1).max(64).nullable(),
});
/**
 * Название встречи. Пустая строка снимает переопределение и возвращает название из Fathom.
 *
 * Правка остаётся в Jisy: у внешнего API Fathom нет ни одного метода записи по встрече —
 * встречи, конспекты и расшифровки отдаются только на чтение, менять там нечего.
 */
export const MEETING_TITLE_MAX = 300;
export const MeetingTitleUpdateSchema = z.object({
    title: z.string().trim().max(MEETING_TITLE_MAX),
});
/**
 * Архив встречи. Не удаление: запись остаётся в Fathom, а в Jisy она лишь уходит из основного
 * списка и возвращается переключателем.
 */
export const MeetingArchiveUpdateSchema = z.object({
    archived: z.boolean(),
});
//# sourceMappingURL=fathom.js.map